import { HelpOutline } from '@/components/icons'
import { SubscribeModal } from '@/components/modal'
import { useProfileViews, useProfileViewsLimited } from '@/hooks/useProfileViews'
import { useUpgradeCta } from '@/hooks/useUpgradeCta'
import { useOptionalUser } from '@/pages/user/selectors'
import { Dialog } from '@headlessui/react'
import moment from 'moment'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePromptDisclosure } from './ProfileUnlockDisclosure'

/** Whether a row press must be intercepted by the unlock prompt. */
function isRowGated(
  enabled: boolean,
  limited: boolean,
  isSelf: boolean,
  unlocked: boolean,
  alreadyUnlocked: boolean
): boolean {
  if (!enabled || !limited || isSelf) return false
  return !unlocked && !alreadyUnlocked
}

/** The row player's standing against the viewer's profile-view quota. */
function useRowUnlockFlags(playerId: number | undefined) {
  const limited = useProfileViewsLimited()
  const { viewedIds, recordView } = useProfileViews()
  const user = useOptionalUser()
  const alreadyUnlocked = !!playerId && viewedIds.includes(playerId)
  const isSelf = !!playerId && playerId === user?.id
  return { limited, alreadyUnlocked, isSelf, recordView }
}

/**
 * Row-level unlock gate for player list rows that navigate to a profile.
 *
 * While the viewer is profile-view limited, the first click on a gated row
 * opens the unlock prompt instead of navigating; unlocking (or an already-held
 * id) reverts the row so the next click runs `onPress`. Inside a
 * {@link ProfileUnlockDisclosureProvider} only one row's prompt is open at a
 * time.
 */
export function useUnlockGate(playerId: number | undefined, enabled: boolean, onPress: () => void) {
  const { limited, alreadyUnlocked, isSelf, recordView } = useRowUnlockFlags(playerId)
  const { promptOpen, openPrompt, closePrompt } = usePromptDisclosure(playerId)
  const [unlocked, setUnlocked] = useState(false)

  const gated = isRowGated(enabled, limited, isSelf, unlocked, alreadyUnlocked)

  const handlePress = () => (gated ? openPrompt() : onPress())

  const handleUnlock = () => {
    if (playerId) recordView(playerId)
    setUnlocked(true)
    closePrompt()
  }

  return { gated, promptOpen, closePrompt, handlePress, handleUnlock }
}

type InfoDialogProps = {
  open: boolean
  onClose: () => void
}

/**
 * "Free profile views" explainer dialog behind the overlay's help icon. The
 * allotment window is rule-configured server-side (daily, monthly, …), so the
 * copy states the count and the server-reported replenish time — never a
 * hardcoded period.
 */
const UnlockInfoDialog = ({ open, onClose }: InfoDialogProps) => {
  const { t } = useTranslation()
  const { viewsLimit, resetAtEpochSeconds } = useProfileViews()
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-white p-6 flex flex-col gap-y-3">
          <Dialog.Title className="text-lg font-semibold text-navy-900">
            {t('player.profileUnlock.infoTitle')}
          </Dialog.Title>
          <p className="text-sm text-navy-900 text-opacity-70">
            {t('player.profileUnlock.info', { count: viewsLimit })}
            {resetAtEpochSeconds != null &&
              ` ${t('player.profileUnlock.resets', { when: moment.unix(resetAtEpochSeconds).fromNow() })}`}
          </p>
          <button
            className="self-end rounded-full bg-navy-900 px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90"
            onClick={onClose}
          >
            {t('common.ok')}
          </button>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

type PromptTextProps = {
  limitReached: boolean
  onUpgrade: () => void
}

/** Overlay middle column: state title plus the upgrade affordance. */
const UnlockPromptText = ({ limitReached, onUpgrade }: PromptTextProps) => {
  const { t } = useTranslation()

  const handleUpgrade = (e: MouseEvent) => {
    e.stopPropagation()
    onUpgrade()
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-sm font-semibold text-white">
        {limitReached ? t('player.profileUnlock.limitReached') : t('player.profileUnlock.title')}
      </span>
      {limitReached ? (
        <span className="truncate text-xs text-white text-opacity-70">
          {t('player.profileUnlock.limitReachedSubtitle')}
        </span>
      ) : (
        <button
          className="w-fit truncate text-xs text-white text-opacity-80 underline hover:text-opacity-100"
          onClick={handleUpgrade}
        >
          {t('player.profileUnlock.upgradeLink')}
        </button>
      )}
    </div>
  )
}

const ViewsLeftPill = ({ viewsRemaining }: { viewsRemaining: number }) => {
  const { t } = useTranslation()
  return (
    <span className="shrink-0 rounded-full bg-white bg-opacity-20 px-2 py-0.5 text-xs font-semibold text-white">
      {t('player.profileUnlock.viewsLeftShort', { count: viewsRemaining })}
    </span>
  )
}

type ActionsProps = {
  limitReached: boolean
  onUnlock: () => void
  onUpgrade: () => void
  onHelp: () => void
}

/** Overlay right side: help icon plus the unlock/upgrade pill. */
const OverlayActions = ({ limitReached, onUnlock, onUpgrade, onHelp }: ActionsProps) => {
  const { t } = useTranslation()

  const handleAction = (e: MouseEvent) => {
    e.stopPropagation()
    if (limitReached) onUpgrade()
    else onUnlock()
  }

  const handleHelp = (e: MouseEvent) => {
    e.stopPropagation()
    onHelp()
  }

  return (
    <>
      <button
        className="shrink-0 text-white text-opacity-70 hover:text-opacity-100"
        aria-label={t('player.profileUnlock.infoTitle')}
        onClick={handleHelp}
      >
        <HelpOutline className="w-4 h-4" />
      </button>
      <button
        className="shrink-0 rounded-full bg-primary-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-opacity-70"
        onClick={handleAction}
      >
        {limitReached ? t('player.profileUnlock.upgrade') : t('player.profileUnlock.unlock')}
      </button>
    </>
  )
}

type OverlayProps = {
  /** Spends a view and reverts the row. */
  onUnlock: () => void
  /** Dismisses back to the normal row without spending. */
  onDismiss: () => void
}

/**
 * The unlock prompt card, rendered as an absolute overlay covering its row
 * (the row must be `relative`). Within the allotment it offers to spend an
 * unlock; at the limit it becomes a DUPR+ upsell.
 */
export const PlayerUnlockOverlay = ({ onUnlock, onDismiss }: OverlayProps) => {
  const { viewsUsed, viewsLimit, viewsLimitReached } = useProfileViews()
  const { openUpgrade, subscribeOpen, closeSubscribe } = useUpgradeCta('profile_limit_cta_clicked')
  const [infoOpen, setInfoOpen] = useState(false)

  const viewsRemaining = Math.max(0, viewsLimit - Math.min(viewsUsed, viewsLimit))

  const handleDismiss = (e: MouseEvent) => {
    e.stopPropagation()
    onDismiss()
  }

  return (
    <div
      className="absolute inset-0 z-10 flex items-center gap-x-2 rounded-2xl bg-navy-900 px-3 cursor-pointer"
      onClick={handleDismiss}
    >
      <ViewsLeftPill viewsRemaining={viewsRemaining} />
      <UnlockPromptText limitReached={viewsLimitReached} onUpgrade={openUpgrade} />
      <OverlayActions
        limitReached={viewsLimitReached}
        onUnlock={onUnlock}
        onUpgrade={openUpgrade}
        onHelp={() => setInfoOpen(true)}
      />
      <UnlockInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
      {/* The subscribe dialog portals its DOM away, but React clicks still bubble
          through this tree — stop them so backdrop clicks can't dismiss-unmount
          the open modal (same guard UnlockInfoDialog carries internally). */}
      <div onClick={(e) => e.stopPropagation()}>
        <SubscribeModal open={subscribeOpen} onClose={closeSubscribe} />
      </div>
    </div>
  )
}
